import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import React from 'react';
import PropTypes from 'prop-types';
import Moment from 'moment';
import { connect } from 'react-redux';
import { Box, Typography, Paper } from '@mui/material';
import { Button, translate as translateConstructor, Spinner } from '@haldor/ui';
import SWAL from 'sweetalert2';
import { getForm, clearForm } from 'actions/templates';
import './LeaveApplicationView.scss';

/**
 * LeaveApplicationView
 * Content-only view for a leave application. Parent is responsible for wrapping in a Modal.
 */
var LeaveApplicationView = function LeaveApplicationView(_ref) {
  var onClose = _ref.onClose,
    application = _ref.application,
    onRevoke = _ref.onRevoke,
    leaveRequests = _ref.leaveRequests,
    translations = _ref.translations,
    fetchForm = _ref.getForm,
    resetForm = _ref.clearForm,
    form = _ref.form,
    modalError = _ref.modalError;
  var translate = translateConstructor(translations);
  var _React$useState = React.useState(false),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    isRevoking = _React$useState2[0],
    setIsRevoking = _React$useState2[1];
  React.useEffect(function () {
    if (application !== null && application !== void 0 && application.formId && fetchForm) {
      fetchForm(application.formId);
    }
    return function () {
      if (resetForm) {
        resetForm();
      }
    };
  }, [application === null || application === void 0 ? void 0 : application.formId, fetchForm, resetForm]);
  var mappedApp = React.useMemo(function () {
    if (!application) return null;
    var LeaveRequest = require('../class/LeaveRequest')["default"];
    var leaveRequestItem = Array.isArray(leaveRequests) ? leaveRequests.find(function (req) {
      return "".concat(req.id) === "".concat(application.id) || "".concat(req.formId) === "".concat(application.formId);
    }) : null;
    if (form && "".concat(form.id) === "".concat(application.formId) && leaveRequestItem) {
      return new LeaveRequest(form, leaveRequestItem).toTableRow();
    }
    if (leaveRequestItem) {
      return new LeaveRequest(null, leaveRequestItem).toTableRow();
    }
    return application;
  }, [application, leaveRequests, form]);
  var lowerStatus = ((mappedApp === null || mappedApp === void 0 ? void 0 : mappedApp.status) || '').toLowerCase();
  var revokeDisabled = !mappedApp || ['withdrawn', 'rejected'].includes(lowerStatus) || new Date(mappedApp.startDate) <= new Date();
  var hasDecision = (mappedApp === null || mappedApp === void 0 ? void 0 : mappedApp.status) && (lowerStatus.includes('approved') || lowerStatus.includes('rejected') || lowerStatus.includes('withdrawn'));
  var handleWithdrawClick = function handleWithdrawClick() {
    if (revokeDisabled) return;
    SWAL.fire({
      title: translate('are-you-sure'),
      text: translate('Withdrawal of the application cannot be undone. Do you want to continue'),
      showCancelButton: true,
      cancelButtonText: translate('Cancel'),
      confirmButtonText: translate('Withdraw')
    }).then(function (result) {
      // support both new `isConfirmed` and legacy `value`
      if (result && (result.isConfirmed || result.value != null)) {
        handleConfirmRevoke();
      }
    });
  };
  var handleConfirmRevoke = /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
      var target;
      return _regeneratorRuntime.wrap(function (_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            setIsRevoking(true);
            _context.prev = 1;
            if (!onRevoke) {
              _context.next = 2;
              break;
            }
            target = mappedApp || application;
            _context.next = 2;
            return onRevoke(target);
          case 2:
            _context.prev = 2;
            setIsRevoking(false);
            return _context.finish(2);
          case 3:
          case "end":
            return _context.stop();
        }
      }, _callee, null, [[1,, 2, 3]]);
    }));
    return function handleConfirmRevoke() {
      return _ref2.apply(this, arguments);
    };
  }();
  var getDecisionColors = function getDecisionColors(decision) {
    var normalizedDecision = (decision || '').toString().toUpperCase();

    // Approved - Green
    if (normalizedDecision.includes('APPROVED')) {
      return {
        badge: '#4BAF4D',
        background: '#F2FEF7'
      };
    }

    // Rejected - Red
    if (normalizedDecision.includes('REJECTED')) {
      return {
        badge: '#DF2D2E',
        background: '#FDEFEE'
      };
    }

    // Withdrawn/Revoked - Gray
    if (normalizedDecision.includes('WITHDRAWN')) {
      return {
        badge: '#757575',
        background: '#F5F5F5'
      };
    }

    // Pending decision - Orange
    if (normalizedDecision.includes('PENDING')) {
      return {
        badge: '#FFA726',
        background: '#FFF4E5'
      };
    }

    // Default fallback
    return {
      badge: '#757575',
      background: '#f5f5f5'
    };
  };
  return /*#__PURE__*/React.createElement(Box, {
    className: "leave-app-modal__inner",
    sx: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%'
    }
  }, /*#__PURE__*/React.createElement(Box, {
    className: "leave-app-modal__content",
    sx: {
      px: 3,
      pt: 2.5,
      pb: 3,
      overflowY: 'auto',
      flex: 1,
      minHeight: 0
    }
  }, mappedApp && /*#__PURE__*/React.createElement(React.Fragment, null, modalError && /*#__PURE__*/React.createElement(Box, {
    sx: {
      mb: 2,
      px: 2,
      py: 1,
      borderRadius: 1,
      backgroundColor: '#DF2D2E'
    }
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    sx: {
      color: '#FFFFFF',
      fontWeight: 500
    }
  }, modalError)), hasDecision && /*#__PURE__*/React.createElement(Paper, {
    sx: {
      p: 1.5,
      mb: 3,
      backgroundColor: getDecisionColors(mappedApp.status).background
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "leave-app-view-field"
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    sx: {
      mb: 0.5,
      fontWeight: 500
    }
  }, /*#__PURE__*/React.createElement("strong", null, translate('Status'))), /*#__PURE__*/React.createElement("span", {
    style: {
      display: 'inline-block',
      padding: '4px 16px',
      borderRadius: 8,
      backgroundColor: getDecisionColors(mappedApp.status).badge,
      color: '#FFFFFF',
      fontWeight: 'bold'
    }
  }, mappedApp.status ? translate(mappedApp.status) : '—')), mappedApp.decisionBy && /*#__PURE__*/React.createElement("div", {
    className: "leave-app-view-field"
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    sx: {
      mb: 0.5,
      fontWeight: 500
    }
  }, /*#__PURE__*/React.createElement("strong", null, translate('Decision by'))), /*#__PURE__*/React.createElement("span", null, mappedApp.decisionBy.firstName, " ", mappedApp.decisionBy.lastName, mappedApp.decisionDate && /*#__PURE__*/React.createElement(React.Fragment, null, " - ", new Date(mappedApp.decisionDate).toISOString().split('T')[0]))), mappedApp.decisionComment && /*#__PURE__*/React.createElement("div", {
    className: "leave-app-view-field leave-app-view-reason"
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    sx: {
      mb: 0.5,
      fontWeight: 500
    }
  }, /*#__PURE__*/React.createElement("strong", null, translate('Decision reasoning'))), /*#__PURE__*/React.createElement("p", null, mappedApp.decisionComment))), /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement("div", {
    className: "leave-app-view-field"
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    sx: {
      mb: 0.5,
      fontWeight: 500
    }
  }, /*#__PURE__*/React.createElement("strong", null, translate('Period'))), /*#__PURE__*/React.createElement("span", null, mappedApp.startDate ? Moment.utc(mappedApp.startDate).local().format('YYYY-MM-DD') : '-', ' – ', mappedApp.endDate ? Moment.utc(mappedApp.endDate).local().format('YYYY-MM-DD') : '-')), /*#__PURE__*/React.createElement("div", {
    className: "leave-app-view-field leave-app-view-reason"
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    sx: {
      mb: 0.5,
      fontWeight: 500
    }
  }, /*#__PURE__*/React.createElement("strong", null, translate('Application sent'))), /*#__PURE__*/React.createElement("p", {
    id: "leave-app-modal-desc"
  }, mappedApp.receivedDate ? Moment.utc(mappedApp.receivedDate).local().format('YYYY-MM-DD') : '-', mappedApp.createdBy && ", ".concat(mappedApp.createdBy.firstName, " ").concat(mappedApp.createdBy.lastName))), /*#__PURE__*/React.createElement("div", {
    className: "leave-app-view-field leave-app-view-reason"
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    sx: {
      mb: 0.5,
      fontWeight: 500
    }
  }, /*#__PURE__*/React.createElement("strong", null, translate('Reason'))), /*#__PURE__*/React.createElement("p", {
    id: "leave-app-modal-desc"
  }, mappedApp.reason || '-'))))), /*#__PURE__*/React.createElement(Box, {
    className: "leave-app-modal__footer",
    sx: {
      px: 3,
      py: 2,
      borderTop: function borderTop(theme) {
        return "1px solid ".concat(theme.palette.divider);
      },
      display: 'flex',
      flexDirection: {
        xs: 'column',
        sm: 'row'
      },
      gap: {
        xs: 2,
        sm: 0
      },
      justifyContent: {
        xs: 'flex-start',
        sm: 'space-between'
      }
    }
  }, /*#__PURE__*/React.createElement(Button, {
    type: "secondary",
    onClick: handleWithdrawClick,
    disabled: revokeDisabled || isRevoking,
    style: {
      minWidth: 140,
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8
    }
  }, isRevoking ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Spinner, {
    small: true
  }), " ", translate('Withdrawing')) : translate('Withdraw')), /*#__PURE__*/React.createElement(Button, {
    type: "primary",
    onClick: onClose,
    disabled: isRevoking,
    style: {
      minWidth: 140
    }
  }, translate('Close'))));
};
LeaveApplicationView.propTypes = {
  onClose: PropTypes.func.isRequired,
  onRevoke: PropTypes.func,
  application: PropTypes.shape({
    id: PropTypes.string,
    startTime: PropTypes.string,
    endTime: PropTypes.string,
    reason: PropTypes.string,
    status: PropTypes.string
  }),
  translations: PropTypes.object.isRequired,
  leaveRequests: PropTypes.array,
  getForm: PropTypes.func,
  clearForm: PropTypes.func,
  form: PropTypes.object,
  modalError: PropTypes.string
};
function mapStateToProps(state) {
  return {
    leaveRequests: state.absence.leaveRequests,
    translations: state.Languages.translations,
    form: state.Templates.form
  };
}
export default connect(mapStateToProps, {
  getForm: getForm,
  clearForm: clearForm
})(LeaveApplicationView);